// src/features/payment/components/PaymentHistoryList.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Payment as PaymentIcon,
  CheckCircle as SuccessIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { fetchPaymentHistory, exportPaymentHistory } from '../api/paymentApi';
import usePaymentStore from '../../../store/paymentStore';

// Interface for payment history item
export interface PaymentHistoryItem {
  id: string;
  amount: number;
  status: 'succeeded' | 'failed' | 'pending' | 'canceled';
  created: string;
  description: string;
  receiptUrl?: string;
  paymentMethod: {
    type: string;
    brand?: string;
    last4?: string;
  };
  currency: string;
  refunded?: boolean;
  refundAmount?: number;
  expiryDate?: string;
}

// Interface for payment history filters
interface PaymentHistoryFilters {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

// Interface for payment history response
interface PaymentHistoryResponse {
  payments: PaymentHistoryItem[];
  total: number;
  summary: {
    totalAmount: number;
    successfulPayments: number;
    failedPayments: number;
    refundedAmount: number;
  };
}

// Status configuration
const statusConfig = {
  succeeded: {
    color: 'success' as const,
    icon: <SuccessIcon fontSize="small" />,
    label: 'Succeeded'
  },
  failed: {
    color: 'error' as const,
    icon: <CancelIcon fontSize="small" />,
    label: 'Failed'
  },
  pending: {
    color: 'warning' as const,
    icon: <PendingIcon fontSize="small" />,
    label: 'Pending'
  },
  canceled: {
    color: 'default' as const,
    icon: <CancelIcon fontSize="small" />,
    label: 'Canceled'
  }
};

const PaymentHistoryList: React.FC = () => {
  // Store state
  const { isLoading, error } = usePaymentStore();
  
  // Local state
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    successfulPayments: 0,
    failedPayments: 0,
    refundedAmount: 0
  });

  // Load payment history on component mount and when filters change
  useEffect(() => {
    loadPaymentHistory();
  }, [page, rowsPerPage, statusFilter, startDate, endDate]);

  // Load payment history from API
  const loadPaymentHistory = async () => {
    try {
      const filters: PaymentHistoryFilters = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };

      const response: PaymentHistoryResponse = await fetchPaymentHistory(filters);
      setPayments(response.payments || []);
      setTotalCount(response.total || 0);
      setSummary(response.summary || {
        totalAmount: 0,
        successfulPayments: 0,
        failedPayments: 0,
        refundedAmount: 0
      });
    } catch (err) {
      console.error('Error loading payment history:', err);
      // Set empty state on error
      setPayments([]);
      setTotalCount(0);
      setSummary({
        totalAmount: 0,
        successfulPayments: 0,
        failedPayments: 0,
        refundedAmount: 0
      });
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadPaymentHistory();
    } catch (err) {
      console.error('Error refreshing payment history:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // Handle date filter changes
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setPage(0);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setPage(0);
  };

  // Clear date filters
  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('all');
    setPage(0);
  };

  // Export payment history
  const handleExport = async () => {
    setExporting(true);
    try {
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };

      await exportPaymentHistory(filters);
    } catch (err) {
      console.error('Error exporting payment history:', err);
    } finally {
      setExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Render status chip
  const renderStatusChip = (status: string, refunded?: boolean) => {
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.failed;
    
    if (refunded && status === 'succeeded') {
      return (
        <Box display="flex" gap={1}>
          <Chip
            label="Refunded"
            color="warning"
            size="small"
            variant="outlined"
          />
          <Chip
            label={config.label}
            color={config.color}
            size="small"
            icon={config.icon}
          />
        </Box>
      );
    }
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  // Render payment method
  const renderPaymentMethod = (paymentMethod: PaymentHistoryItem['paymentMethod']) => {
    if (paymentMethod.type === 'card' && paymentMethod.brand && paymentMethod.last4) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} •••• {paymentMethod.last4}
          </Typography>
        </Box>
      );
    }
    
    if (paymentMethod.type && paymentMethod.type !== 'unknown') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {paymentMethod.type.replace('_', ' ')}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <PaymentIcon fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.disabled">
          Payment method unavailable
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Payment History
        </Typography>
        
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={20} /> : <ExportIcon />}
            onClick={handleExport}
            disabled={isLoading || exporting}
          >
            Export CSV
          </Button>
          
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading || refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Paid
              </Typography>
              <Typography variant="h5" color="primary">
                {formatCurrency(summary.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Successful Payments
              </Typography>
              <Typography variant="h5" color="success.main">
                {summary.successfulPayments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Failed Payments
              </Typography>
              <Typography variant="h5" color="error.main">
                {summary.failedPayments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Refunded
              </Typography>
              <Typography variant="h5" color="warning.main">
                {formatCurrency(summary.refundedAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterIcon color="action" />
          <Typography variant="h6">Filters</Typography>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="succeeded">Succeeded</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDate
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!startDate && !endDate && statusFilter === 'all'}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Payment History Table */}
      <Paper>
        {isLoading && !refreshing ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Credit Expiry</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box py={4} display="flex" flexDirection="column" alignItems="center">
                          <PaymentIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" gutterBottom>
                            No payments found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {statusFilter !== 'all' || startDate || endDate
                              ? 'No payments match your current filters'
                              : 'You haven\'t made any payments yet'}
                          </Typography>
                          {statusFilter !== 'all' || startDate || endDate ? (
                            <Button 
                              variant="outlined" 
                              onClick={handleClearFilters}
                              sx={{ mt: 2 }}
                            >
                              Clear Filters
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          <Tooltip title={payment.id}>
                            <span>{formatDate(payment.created)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {formatCurrency(payment.amount, payment.currency)}
                            </Typography>
                            {payment.refunded && payment.refundAmount && (
                              <Typography variant="caption" color="warning.main">
                                -{formatCurrency(payment.refundAmount, payment.currency)} refunded
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {payment.description || 'Credit purchase'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {renderPaymentMethod(payment.paymentMethod)}
                        </TableCell>
                        <TableCell>
                          {renderStatusChip(payment.status, payment.refunded)}
                        </TableCell>
                        <TableCell>
                          {payment.expiryDate && payment.status === 'succeeded' ? (
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(payment.expiryDate)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1}>
                            {/* Receipt download button */}
                            {payment.receiptUrl && (
                              <Tooltip title="Download receipt">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(payment.receiptUrl, '_blank')}
                                  color="primary"
                                >
                                  <ReceiptIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* If no receipt available, show message */}
                            {!payment.receiptUrl && payment.status === 'succeeded' && (
                              <Tooltip title="Receipt not available">
                                <IconButton size="small" disabled>
                                  <ReceiptIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <Divider />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* Help Text */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          About Payment History
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This page shows all payments made to add credits to your account. Each successful payment 
          will have a receipt available for download. Receipts are useful for expense reporting and 
          tax purposes.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Credit Expiry:</strong> Credits from successful payments expire one year from the 
          payment date. Make sure to use your credits before they expire.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> This shows payment transactions only. For API usage history, 
          visit the <Button color="primary" size="small" href="/usage">Usage Statistics</Button> page.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PaymentHistoryList;